// styleResolver — pure functions for resolving node and connection visual styles.
// Merges topology-provided styles over built-in defaults, with unknown-type fallback.
// This is the single source of truth for how types map to visual properties.

import type { NodeTypeStyleDef, ConnectionStyleDef, LaneDef } from '../types/index.ts';

// ---------------------------------------------------------------------------
// Classification → color mapping for lanes without explicit color
// ---------------------------------------------------------------------------

const CLASSIFICATION_COLORS: Record<string, string> = {
  core: '#7B68EE',
  Core: '#7B68EE',
  supporting: '#4A90D9',
  Supporting: '#4A90D9',
  generic: '#94a3b8',
  Generic: '#94a3b8',
  external: '#e879f9',
  External: '#e879f9',
  branch: '#EF4444',
};

/** Resolve a lane's display color — uses explicit color if available, falls back to classification. */
export function resolveLaneColor(lane: LaneDef): string {
  if (lane.color) return lane.color;
  return CLASSIFICATION_COLORS[lane.classification] ?? '#94a3b8';
}

// ---------------------------------------------------------------------------
// Built-in defaults (Journey A prototype values — backward compatibility)
// ---------------------------------------------------------------------------

export const DEFAULT_NODE_STYLES: Record<string, NodeTypeStyleDef> = {
  action: { icon: '●', fill: '#f59e0b', label: 'User Action' },
  command: { icon: '▶', fill: '#38bdf8', label: 'Command' },
  event: { icon: '◆', fill: '#34d399', label: 'Domain Event' },
  sagaEvent: { icon: '◈', fill: '#a78bfa', label: 'Saga Event' },
  coachingEvent: { icon: '◇', fill: '#fb923c', label: 'Coaching Event' },
  policy: { icon: '⟳', fill: '#f472b6', label: 'Policy' },
  process: { icon: '⚙', fill: '#94a3b8', label: 'Process' },
  projection: { icon: '▣', fill: '#22d3ee', label: 'Projection' },
  saga: { icon: '◎', fill: '#c4b5fd', label: 'Saga State' },
  terminal: { icon: '⊘', fill: '#ef4444', label: 'Terminal' },
  branch: { icon: '⬦', fill: '#fde047', label: 'Branch' },
  output: { icon: '☁', fill: '#a3e635', label: 'Output' },
  externalInput: { icon: '⬡', fill: '#e879f9', label: 'External Input' },
};

export const DEFAULT_CONNECTION_STYLES: Record<string, ConnectionStyleDef> = {
  happy: { color: '#4ade80', dash: false, alwaysVisible: true },
  cross: { color: '#818cf8', dash: true, alwaysVisible: true },
  branch: { color: '#f59e0b', dash: true, alwaysVisible: false },
  return: { color: '#22d3ee', dash: true, alwaysVisible: true },
  'parse-fail': { color: '#ef4444', dash: true, alwaysVisible: false },
  coaching: { color: '#fb923c', dash: true, alwaysVisible: false },
  'coaching-abandon': { color: '#f87171', dash: true, alwaysVisible: false },
  disagreement: { color: '#fbbf24', dash: true, alwaysVisible: false },
};

const DEFAULT_NODE_RX: Record<string, number> = {
  branch: 10,
  terminal: 3,
};

const UNKNOWN_NODE_STYLE: NodeTypeStyleDef = { icon: '?', fill: '#888', label: 'Unknown' };
const UNKNOWN_CONNECTION_STYLE: ConnectionStyleDef = { color: '#4ade8080', dash: false };

// ---------------------------------------------------------------------------
// Resolvers
// ---------------------------------------------------------------------------

export function resolveNodeStyle(
  nodeType: string,
  topologyStyles?: Record<string, NodeTypeStyleDef>,
): NodeTypeStyleDef {
  return (
    topologyStyles?.[nodeType] ??
    DEFAULT_NODE_STYLES[nodeType] ?? { ...UNKNOWN_NODE_STYLE, label: nodeType }
  );
}

export function resolveConnectionStyle(
  connStyle: string,
  topologyStyles?: Record<string, ConnectionStyleDef>,
): ConnectionStyleDef {
  return (
    topologyStyles?.[connStyle] ?? DEFAULT_CONNECTION_STYLES[connStyle] ?? UNKNOWN_CONNECTION_STYLE
  );
}

export function isConnectionAlwaysVisible(
  connStyle: string,
  topologyStyles?: Record<string, ConnectionStyleDef>,
): boolean {
  const resolved = topologyStyles?.[connStyle] ?? DEFAULT_CONNECTION_STYLES[connStyle];
  return resolved?.alwaysVisible === true;
}

export function resolveNodeRx(
  nodeType: string,
  topologyStyles?: Record<string, NodeTypeStyleDef>,
): number {
  return topologyStyles?.[nodeType]?.rx ?? DEFAULT_NODE_RX[nodeType] ?? 5;
}
